import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  effect,
  forwardRef,
  ElementRef,
  viewChild,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface MultiSelectOption {
  id: any;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-custom-multiselect',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './custom-multiselect.component.html',
  styleUrls: ['./custom-multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMultiselectComponent),
      multi: true,
    },
  ],
})
export class CustomMultiselectComponent implements ControlValueAccessor {
  // Inputs
  options = input<MultiSelectOption[]>([]);
  placeholder = input<string>('Select...');
  disabledInput = input(false, { alias: 'disabled' });
  private _disabledFromCVA = signal<boolean>(false);
  disabled = computed(() => this.disabledInput() || this._disabledFromCVA());
  multiple = input<boolean>(true);
  searchable = input<boolean>(true);
  maxHeight = input<string>('300px');
  selected = input<any[]>([]);

  // Outputs
  selectionChange = output<any[]>();

  // Internal state
  isOpen = signal<boolean>(false);
  searchTerm = signal<string>('');
  selectedValues = signal<any[]>([]);

  // ViewChild for search input
  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  // Computed values
  filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const opts = this.options();
    console.log('CustomMultiselect - filteredOptions computed:', {
      optionsLength: opts.length,
      searchTerm: term,
      optionsSample: opts.slice(0, 3),
      firstOption: opts[0],
      hasIdAndLabel:
        opts.length > 0 ? { hasId: 'id' in opts[0], hasLabel: 'label' in opts[0] } : 'no options',
    });

    if (!term) return opts;

    return opts.filter(opt => opt.label.toLowerCase().includes(term));
  });

  selectedOptions = computed(() => {
    const selected = this.selectedValues();
    const opts = this.options();
    return opts.filter(opt => selected.includes(opt.id));
  });

  displayText = computed(() => {
    const selected = this.selectedOptions();
    if (selected.length === 0) return this.placeholder();
    if (!this.multiple() && selected.length === 1) return selected[0].label;
    if (this.multiple() && selected.length > 5) {
      return `${selected.length} items selected`;
    }
    return selected.map(s => s.label).join(', ');
  });

  // Select all visible
  selectAllVisible(): void {
    const allIds = this.filteredOptions()
      .filter(opt => !opt.disabled)
      .map(opt => opt.id);
    this.selectedValues.set(allIds);
  }
  // Deselect all
  clearAllVisible(): void {
    this.selectedValues.set([]);
  }
  // Select all toggle
  toggleSelectAll(event: Event): void {
    event.stopPropagation();
    const allSelected = this.areAllVisibleSelected();
    if (allSelected) {
      this.clearAllVisible();
    } else {
      this.selectAllVisible();
    }
  }
  areAllVisibleSelected(): boolean {
    const visibleIds = this.filteredOptions()
      .filter(opt => !opt.disabled)
      .map(opt => opt.id);
    return visibleIds.length > 0 && visibleIds.every(id => this.selectedValues().includes(id));
  }

  // Overload toggleOption to accept event
  toggleOption(option: MultiSelectOption, event?: Event): void {
    if (option.disabled) return;
    if (event) event.stopPropagation();
    const currentValues = [...this.selectedValues()];
    const index = currentValues.indexOf(option.id);
    if (this.multiple()) {
      if (index > -1) {
        currentValues.splice(index, 1);
      } else {
        currentValues.push(option.id);
      }
      this.selectedValues.set(currentValues);
    } else {
      this.selectedValues.set([option.id]);
      this.closeDropdown();
    }
  }

  // ControlValueAccessor
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Auto-emit selection changes
    // Sync selected input to internal signal
    effect(() => {
      const extSelected = this.selected();
      if (extSelected && Array.isArray(extSelected)) {
        this.selectedValues.set([...extSelected]);
      }
    });
    // Emit selection changes
    effect(() => {
      const values = this.selectedValues();
      this.selectionChange.emit(values);
      this.onChange(this.multiple() ? values : values[0] || null);
    });
  }

  // Close dropdown when clicking outside
  @HostListener('document:click')
  onDocumentClick(): void {
    console.log('Document click detected, isOpen:', this.isOpen());
    if (this.isOpen()) {
      this.closeDropdown();
    }
  }

  // Toggle dropdown
  toggleDropdown(event?: Event): void {
    console.log(
      'toggleDropdown called, disabled:',
      this.disabled(),
      'current isOpen:',
      this.isOpen()
    );
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled()) return;

    const newState = !this.isOpen();
    console.log('Setting isOpen to:', newState);
    this.isOpen.set(newState);

    if (newState && this.searchable()) {
      setTimeout(() => {
        const input = this.searchInput();
        if (input) {
          input.nativeElement.focus();
        }
      }, 50);
    } else {
      this.searchTerm.set('');
    }
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    this.searchTerm.set('');
    this.onTouched();
  }

  isSelected(id: any): boolean {
    return this.selectedValues().includes(id);
  }

  removeItem(id: any, event: Event): void {
    event.stopPropagation();
    const currentValues = this.selectedValues().filter(v => v !== id);
    this.selectedValues.set(currentValues);
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (Array.isArray(value)) {
      this.selectedValues.set([...value]);
    } else if (value != null) {
      this.selectedValues.set([value]);
    } else {
      this.selectedValues.set([]);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledFromCVA.set(isDisabled);
  }
}
